import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { usePwaStandalone } from "@/lib/pwa";
import { LogOut, ShieldCheck, User, BadgeCheck, Menu, X, LayoutDashboard, Music2, Download } from "lucide-react";

export default function Navbar() {
  const { user, logout, hasActiveLicense } = useAuth();
  const { t } = useI18n();
  const isInstalledApp = usePwaStandalone();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const linkClass = ({ isActive }) =>
    `whitespace-nowrap text-sm leading-none tracking-wide transition-colors ${
      isActive ? "text-[#D4A23A]" : "text-zinc-400 hover:text-white"
    }`;
  const mobileLinkClass = ({ isActive }) =>
    `block rounded-xl px-3 py-3 text-sm font-semibold transition ${
      isActive ? "bg-[#D4A23A]/15 text-[#D4A23A]" : "text-zinc-300 hover:bg-white/5 hover:text-white"
    }`;

  const close = () => setOpen(false);
  const scrollHomeTop = () => {
    setOpen(false);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  };
  const openInstallPrompt = () => {
    if (isInstalledApp) return;
    window.dispatchEvent(new Event("stockmusic:open-install-prompt"));
    setOpen(false);
  };
  const doLogout = () => {
    logout();
    setOpen(false);
    nav("/");
  };

  return (
    <>
    <header data-testid="app-navbar" className="sticky top-0 z-40 border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <Link to="/" onClick={scrollHomeTop} data-testid="nav-logo" className="flex min-w-0 shrink-0 items-center gap-2">
          <img
            src="/brand/stockmusic-app-logo.png"
            alt="StockMusic.ro"
            className="h-10 w-10 rounded-xl object-cover shadow-[0_0_24px_rgba(212,162,58,0.25)]"
          />
          <span className="hidden font-display text-lg font-black tracking-tight sm:inline xl:text-xl">
            STOCK<span className="text-[#D4A23A]">MUSIC</span>RO
          </span>
        </Link>

        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-4 lg:flex xl:gap-7">
          <NavLink to="/" end className={linkClass} onClick={scrollHomeTop} data-testid="nav-home">
            {t("hero.cta1")}
          </NavLink>
          {["admin", "superadmin"].includes(user?.role) && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <ShieldCheck size={14} /> {t("nav.admin")}
              </span>
            </NavLink>
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {!isInstalledApp && (
            <button
              type="button"
              onClick={openInstallPrompt}
              className="hidden items-center gap-1.5 rounded-full border border-[#D4A23A]/30 bg-[#D4A23A]/10 px-3 py-2 text-xs font-semibold text-[#D4A23A] transition hover:bg-[#D4A23A] hover:text-black md:flex"
              title={t("install.action")}
            >
              <Download size={14} /> {t("nav.installApp")}
            </button>
          )}
          {user ? (
            <>
              {hasActiveLicense && (
                <span className="hidden items-center gap-1 rounded-full border border-[#D4A23A]/30 bg-[#D4A23A]/10 px-2.5 py-1 text-[10px] uppercase tracking-wider text-[#D4A23A] sm:flex">
                  <BadgeCheck size={12} /> {t("nav.licensed")}
                </span>
              )}
              <div className="hidden items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 sm:flex">
                <User size={13} className="text-zinc-400" />
                <span data-testid="user-name" className="max-w-[140px] truncate text-xs text-zinc-300">
                  {user.business_name}
                </span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={doLogout}
                className="rounded-full p-2 text-zinc-400 transition hover:bg-white/5 hover:text-white"
                title={t("nav.logout")}
              >
                <LogOut size={15} />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login-btn" to="/login" className="whitespace-nowrap text-sm text-zinc-300 hover:text-white">
                {t("nav.signin")}
              </Link>
              <Link
                data-testid="nav-register-btn"
                to="/register"
                className="whitespace-nowrap rounded-full bg-[#D4A23A] px-4 py-2 text-sm font-semibold text-black transition hover:bg-[#E2B85C]"
              >
                {t("nav.getStarted")}
              </Link>
            </>
          )}
          <button
            type="button"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-white/10 p-2 text-zinc-300 transition hover:bg-white/5 hover:text-white lg:hidden"
            aria-label="Meniu"
            aria-expanded={open}
          >
            {open ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </div>
      <div className="hidden border-t border-white/5 bg-[#080808]/90 md:block" data-testid="primary-action-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 overflow-x-auto px-4 py-2 sm:px-6 lg:px-8">
          <PrimaryNavLink to="/music" icon={Music2} label={t("nav.musicAccess")} testid="nav-music-primary" />
          {user && (
            <PrimaryNavLink to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboardAccess")} testid="nav-dashboard-primary" />
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-white/5 bg-[#0A0A0A] px-4 py-3 shadow-2xl lg:hidden" data-testid="mobile-menu">
          <nav className="space-y-1">
            <NavLink
              to="/music"
              className={({ isActive }) => `flex items-center justify-center gap-2 rounded-2xl px-4 py-4 text-sm font-black transition ${
                isActive ? "bg-[#E2B85C] text-black" : "bg-[#D4A23A] text-black hover:bg-[#E2B85C]"
              }`}
              onClick={close}
            >
              <Music2 size={18} /> {t("nav.musicAccess")}
            </NavLink>
            {user && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `mb-2 flex items-center justify-center gap-2 rounded-2xl border px-4 py-4 text-sm font-black transition ${
                  isActive
                    ? "border-[#D4A23A] bg-[#D4A23A]/20 text-[#D4A23A]"
                    : "border-[#D4A23A]/35 bg-[#D4A23A]/10 text-[#D4A23A] hover:bg-[#D4A23A]/15"
                }`}
                onClick={close}
              >
                <LayoutDashboard size={18} /> {t("nav.dashboardAccess")}
              </NavLink>
            )}
            <NavLink to="/" end className={mobileLinkClass} onClick={scrollHomeTop}>{t("hero.cta1")}</NavLink>
            {!isInstalledApp && (
              <button
                type="button"
                onClick={openInstallPrompt}
                className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-semibold text-[#D4A23A] hover:bg-[#D4A23A]/10"
              >
                <Download size={15} /> {t("nav.installApp")}
              </button>
            )}
            {user ? (
              <>
                {["admin", "superadmin"].includes(user.role) && (
                  <NavLink to="/admin" className={mobileLinkClass} onClick={close}>
                    <span className="flex items-center gap-2"><ShieldCheck size={15} /> {t("nav.admin")}</span>
                  </NavLink>
                )}
                <button
                  type="button"
                  onClick={doLogout}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm font-semibold text-zinc-300 hover:bg-white/5 hover:text-white"
                >
                  <LogOut size={15} /> {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <NavLink to="/login" className={mobileLinkClass} onClick={close}>{t("nav.signin")}</NavLink>
                <NavLink to="/register" className={mobileLinkClass} onClick={close}>{t("nav.getStarted")}</NavLink>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
    {user && (
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-[#0A0A0A]/95 px-2 pb-[max(env(safe-area-inset-bottom),8px)] pt-2 backdrop-blur-xl sm:hidden" data-testid="mobile-bottom-nav">
        <div className={`grid gap-1 ${isInstalledApp ? "grid-cols-2" : "grid-cols-3"}`}>
          <BottomNavLink to="/music" label={t("nav.music")} icon={Music2} primary />
          <BottomNavLink to="/dashboard" label="Licente" icon={LayoutDashboard} primary />
          {!isInstalledApp && (
            <button
              type="button"
              onClick={openInstallPrompt}
              className="flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-[#D4A23A] transition hover:bg-[#D4A23A]/10"
            >
              <Download size={18} />
              <span className="max-w-full truncate px-1">{t("nav.app")}</span>
            </button>
          )}
        </div>
      </nav>
    )}
    </>
  );
}

function PrimaryNavLink({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) => `inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-black transition ${
        isActive
          ? "bg-[#E2B85C] text-black"
          : "border border-[#D4A23A]/35 bg-[#D4A23A]/10 text-[#D4A23A] hover:bg-[#D4A23A] hover:text-black"
      }`}
    >
      <Icon size={16} /> {label}
    </NavLink>
  );
}

function BottomNavLink({ to, label, icon: Icon, primary = false }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold transition ${
        primary
          ? (isActive ? "bg-[#E2B85C] text-black" : "bg-[#D4A23A] text-black hover:bg-[#E2B85C]")
          : (isActive ? "bg-[#D4A23A]/15 text-[#D4A23A]" : "text-zinc-400 hover:bg-white/5 hover:text-white")
      }`}
    >
      <Icon size={18} />
      <span className="max-w-full truncate px-1">{label}</span>
    </NavLink>
  );
}
